import {
  useCallback,
  type FC
} from 'react';
import {
  Box,
  Stack,
  Button,
  Typography,
  styled,
} from '@mui/joy';
import {
  type PaymentMethod
} from '../model';

type PaymentMethodItemProps = {
  hint: string;
  iconSrc: Url;
  onClick?(): void;
  darkMode?: Maybe<boolean>;
};

const PaymentMethodItem: FC<PaymentMethodItemProps> = ({
  hint,
  iconSrc,
  onClick,
  darkMode,
}) => (
  <Button
    color='neutral'
    onClick={onClick}
    sx={({ palette }) => ({
      gap: '8px',
      height: 132,
      padding: 1.5,
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
      justifyContent: 'space-between',
      overflow: 'hidden',
      border: 'none',
      ...(darkMode && ({ backgroundColor: palette.common[475] })),
    })}>
    <Box
      component='img'
      loading='lazy'
      src={iconSrc}
      sx={({ breakpoints }) => ({
        maxHeight: 80,
        maxWidth: 180,
        height: 'auto',
        width: '100%',
        objectFit: 'contain',
        [breakpoints.down(441)]: { maxWidth: 140 }
      })}
    />
    <Typography
      sx={{
        fontSize: 14,
        fontWeight: 400,
        lineHeight: '20px',
        fontStyle: 'normal',
      }}>
      {hint}
    </Typography>
  </Button>
);

const LabelText = styled('div')(({ theme }) => ({
  fontSize: 18,
  fontWeight: 500,
  lineHeight: '28px',
  fontStyle: 'normal',
  color: theme.palette.common.white,
}));

export type PaymentMethodsProps = {
  label?: Maybe<string>;
  items?: Array<PaymentMethod>;
  darkMode?: Maybe<boolean>;
  onSelect?(method: PaymentMethod): void;
};

export const PaymentMethods: FC<PaymentMethodsProps> = ({
  items,
  label,
  onSelect,
  darkMode,
}) => {

  const handleSelectPaymentMethod = useCallback((paymentMethod: PaymentMethod) => (): void => {
    onSelect?.(paymentMethod);
  }, [onSelect]);

  return (
    <Stack
      gap='8px'
      sx={({ palette }) => ({
        ...(darkMode && ({ backgroundColor: palette.common[900] })),
      })}>
      <LabelText>
        {label}
      </LabelText>
      <Box
        sx={{
          gap: 1,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
        }}>
        {items?.map(({ id, displayName, iconSrc, ...props }) => (
          <PaymentMethodItem
            key={id}
            hint={displayName}
            iconSrc={iconSrc}
            darkMode={darkMode}
            onClick={handleSelectPaymentMethod({ id, displayName, iconSrc, ...props })}
          />
        ))}
      </Box>
    </Stack>
  );
};